<template>
  <div class="kb-main">
    <div class="h-full">
      <!-- FIX 1: Removed '&& localKanbanCols.length' so the 'Add Column' button is visible even on 0 columns -->
      <div v-if="localKanbanCols" class="h-full">
        
        <!-- FIX 2: Added handle=".column-drag-handle" -->
        <draggable :list="localKanbanCols" group="kb-col" :move="checkColMove" class="vx-row pb-4 bg-main h-full"
          style="overflow-x: auto; overflow-y: hidden; flex-wrap: nowrap" :scroll-sensitivity="200"
          :force-fallback="true"
          handle=".column-drag-handle">
          
          <div v-for="(col, index) in localKanbanCols" :key="col._id || 'col-' + index" class="vx-col flex flex-col h-full">
            
            <!-- 🟢 START OF DRAGGABLE AREA: Only this wrapper can be grabbed -->
            <div class="column-drag-handle flex flex-col" style="cursor: grab;">

              <!-- COLUMN TITLE HEADER -->
              <div class="kb-col-head flex items-center justify-between mb-2 bg-white"
                :class="index === editingIndex ? 'py-2 pl-4' : ''">
                
                <div class="text-left flex-grow pr-2">
                  <h4 v-if="index !== editingIndex" class="text-left text-gray-800">
                    {{ col.title }}
                  </h4>

                  <vs-input v-else class="w-full text-left" v-model="tempColTitle" @keyup.enter="saveColEdits(editingIndex)" :autofocus="true" size="large" />
                </div>

                <div class="flex flex-shrink-0">
                  <div>
                    <vx-tooltip text="Edit" position="bottom">
                      <feather-icon icon="Edit2Icon" svgClasses="w-5 h-5 cursor-pointer stroke-current text-primary"
                        @click.stop="editCol(col, index)" />
                    </vx-tooltip>
                  </div>
                  <div v-if="index !== 0">
                    <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
                      <a class="a-icon cursor-pointer text-dark ml-1" href="#">
                        <feather-icon icon="MoreVerticalIcon" class="cursor-pointer" svgClasses="w-5 h-5" />
                      </a>
                      <vs-dropdown-menu>
                        <vs-dropdown-item
                          @click.stop="userCardView === 'minimized' && !userCardViewMaxColIndex.includes(index) ? userCardViewMaxColIndex.push(index) : removeUserCardViewMaxColIndex(index)">
                          <div v-if="userCardView === 'minimized' && !userCardViewMaxColIndex.includes(index)" style="display: flex; align-items: baseline" class="items-center">
                            Classic View
                          </div>
                          <div v-else style="display: flex; align-items: baseline" class="items-center">
                            Compact View
                          </div>
                        </vs-dropdown-item>
                        
                        <vs-dropdown-item style="min-width: 11rem; font-size: 14.5px">
                          <vx-tooltip v-if="localKanbanCols[index].colUsersData && localKanbanCols[index].colUsersData.length > 0" text="Only empty columns can be deleted" position="bottom">
                            <div style="opacity: 0.5; cursor: not-allowed;">Delete Column</div>
                          </vx-tooltip>
                          <div v-else @click.stop="deleteColumn(index)" class="text-danger font-medium">
                            Delete Column
                          </div>
                        </vs-dropdown-item>
                      </vs-dropdown-menu>
                    </vs-dropdown>
                  </div>
                </div>
              </div>

            <!-- COLUMN DESCRIPTION & TRIGGERS -->
            <div v-if="col.description || index === editingIndex || (col.colData && col.colData.type)" class="org-card kb-col break-all text-left w-full">

              <!-- VIEW MODE -->
              <div v-if="index !== editingIndex">
                
                <!-- Display Trigger based on its type -->
                <div v-if="col.colData && col.colData.type" class="mb-2 bg-gray-100 p-2 rounded flex items-center gap-2 overflow-hidden">
                  
                  <!-- Apps -->
                  <template v-if="col.colData.type === 'apps' && col.colData.appSelected">
                    <img v-if="col.colData.appSelected === 'coach' || col.colData.appSelected === 'mentor'" src="@/assets/images/coach.svg" class="w-5 h-5 flex-shrink-0" />
                    <img v-else-if="getSafeApp(col.colData.appSelected)" :src="getSafeApp(col.colData.appSelected).icon" class="w-5 h-5 flex-shrink-0" />
                    <span v-if="getAppFlow(col.colData.appSelected, col.colData.appStepData)" class="text-xs font-semibold text-gray-700 truncate">
                      Action: {{ getAppFlow(col.colData.appSelected, col.colData.appStepData).title }}
                    </span>
                  </template>

                  <!-- Custom Link / Attachment -->
                  <template v-else-if="col.colData.type === 'customLink'">
                    <!-- Show Paperclip for files, Link for URLs -->
                    <feather-icon :icon="col.colData.attachmentName ? 'PaperclipIcon' : 'LinkIcon'" class="w-4 h-4 text-gray-600 flex-shrink-0" />
                    
                    <a v-if="col.colData.customLink" :href="col.colData.customLink" target="_blank" class="text-xs font-semibold text-primary truncate underline">
                      <!-- Prioritize showing the File Name over the raw URL -->
                      {{ col.colData.attachmentName || col.colData.customLink }}
                    </a>
                    <span v-else-if="col.colData.attachmentName" class="text-xs font-semibold text-gray-700 truncate">
                      {{ col.colData.attachmentName }}
                    </span>
                  </template>

                  <!-- Social Connections -->
                  <template v-else-if="col.colData.type === 'socialConnections'">
                    <feather-icon icon="Share2Icon" class="w-4 h-4 text-gray-600 flex-shrink-0" />
                    <span class="text-xs font-semibold text-gray-700 truncate">
                      Action: {{ getSocialStepById(col.colData.socialStepData) ? getSocialStepById(col.colData.socialStepData).title : 'Social' }}
                    </span>
                  </template>

                </div>

                <p v-if="col.description" class="text-grey-600 text-left block w-full mt-1">
                  <span class="text-sm font-semibold text-grey-800">Description:</span> 
                  <span class="text-sm ml-1 html-content" v-html="col.description"></span>
                </p>

              </div>

              <!-- EDIT MODE -->
              <div v-else class="w-full mb-5 mt-1 text-left">
                
                <div v-if="isPaidCoachBoard" class="mb-3 bg-gray-50 p-2 rounded border border-gray-200">
                  
                  <!-- DISPLAY SELECTED TRIGGER BEFORE SAVING -->
                  <div v-if="tempColData && tempColData.type" class="flex items-center justify-between">
                    
                    <div class="flex items-center gap-2" v-if="tempColData.type === 'apps' && getSafeApp(tempColData.appSelected)">
                      <img v-if="tempColData.appSelected === 'coach' || tempColData.appSelected === 'mentor'" src="@/assets/images/coach.svg" class="w-6 h-6" />
                      <img v-else :src="getSafeApp(tempColData.appSelected).icon" class="w-6 h-6" />
                      <span class="text-sm font-semibold text-gray-700" v-if="getAppFlow(tempColData.appSelected, tempColData.appStepData)">
                        {{ getAppFlow(tempColData.appSelected, tempColData.appStepData).title }}
                      </span>
                    </div>

                    <div class="flex items-center gap-2" v-else-if="tempColData.type === 'customLink'">
                      <!-- Show Paperclip for files, Link for URLs -->
                      <feather-icon :icon="tempColData.attachmentName ? 'PaperclipIcon' : 'LinkIcon'" class="w-4 h-4 text-gray-600" />
                      
                      <span class="text-sm font-semibold text-gray-700 truncate" style="max-width: 150px;">
                        <!-- Prioritize showing the File Name over the raw URL -->
                        {{ tempColData.attachmentName || tempColData.customLink }}
                      </span>
                    </div>

                    <div class="flex items-center gap-2" v-else-if="tempColData.type === 'socialConnections'">
                       <feather-icon icon="Share2Icon" class="w-4 h-4 text-gray-600" />
                       <span class="text-sm font-semibold text-gray-700 truncate">
                         {{ getSocialStepById(tempColData.socialStepData) ? getSocialStepById(tempColData.socialStepData).title : 'Social' }}
                       </span>
                    </div>

                    <feather-icon icon="Trash2Icon" class="cursor-pointer text-danger w-4 h-4" @click="removeAppTrigger()" />
                  </div>

                  <!-- TRIGGER BUTTON -->
                  <div v-else>
                    <div class="flex items-center gap-2 cursor-pointer text-primary bg-white border border-gray-300 w-fit px-3 py-1 rounded-full shadow-sm" 
                         @click="showTriggerMenu = !showTriggerMenu; resetTriggerState()">
                      <feather-icon icon="PlusIcon" class="w-4 h-4" />
                      <span class="text-sm font-semibold">Trigger</span>
                    </div>
                  </div>

                  <!-- 3-OPTION TRIGGER MENU -->
                  <div v-if="showTriggerMenu && !tempColData.type" class="mt-3 border rounded bg-white shadow-sm p-2 relative z-50">
                    
                    <!-- Trigger Grid Selection -->
                    <div v-if="!selectedTriggerCategory" class="flex flex-wrap border-t border-l border-gray-200">
                      <div class="w-1/3 border-r border-b border-gray-200 py-4 cursor-pointer hover:bg-gray-50 flex flex-col items-center justify-center gap-1"
                           v-for="opt in triggerOptions" :key="opt.id" @click="selectedTriggerCategory = opt.id">
                           <feather-icon v-if="opt.id === 'customLink'" icon="LinkIcon" class="w-6 h-6 text-gray-600" />
                           <feather-icon v-else-if="opt.id === 'socialConnections'" icon="Share2Icon" class="w-6 h-6 text-gray-600" />
                           <feather-icon v-else-if="opt.id === 'apps'" icon="GridIcon" class="w-6 h-6 text-gray-600" />
                           <p class="text-xs font-semibold text-gray-700 mt-1">{{ opt.name }}</p>
                      </div>
                    </div>

                    <!-- Custom Link & Attachment Flow -->
                    <div v-if="selectedTriggerCategory === 'customLink'">
                      
                      <!-- Back to Root Menu -->
                      <div v-if="!customLinkSubCategory" class="flex items-center gap-2 p-1.5 mb-1 border-b cursor-pointer text-primary text-sm font-semibold hover:bg-gray-50 rounded" @click="selectedTriggerCategory = ''">
                        <feather-icon icon="ArrowLeftIcon" class="w-4 h-4" /> Back
                      </div>
                      
                      <!-- Back to Custom Link Menu -->
                      <div v-else class="flex items-center gap-2 p-1.5 mb-1 border-b cursor-pointer text-primary text-sm font-semibold hover:bg-gray-50 rounded" @click="customLinkSubCategory = ''">
                        <feather-icon icon="ArrowLeftIcon" class="w-4 h-4" /> Back
                      </div>
                      
                      <!-- Menu Options (Styled like Social/Apps) - REMOVED SCROLL CLASSES -->
                      <div v-if="!customLinkSubCategory" class="flex flex-col gap-1">
                         <div class="flex items-center gap-3 p-1.5 hover:bg-gray-100 cursor-pointer rounded" @click="customLinkSubCategory = 'link'">
                           <feather-icon icon="LinkIcon" class="w-5 h-5 text-gray-700" />
                           <span class="text-sm font-medium text-gray-700">Add Link</span>
                         </div>
                         <div class="flex items-center gap-3 p-1.5 hover:bg-gray-100 cursor-pointer rounded" @click="customLinkSubCategory = 'attachment'">
                           <feather-icon icon="PaperclipIcon" class="w-5 h-5 text-gray-700" />
                           <span class="text-sm font-medium text-gray-700">Add Attachment</span>
                         </div>
                      </div>

                      <!-- Input Form for Link -->
                      <div v-if="customLinkSubCategory === 'link'" class="flex flex-col gap-2 mt-1">
                         <vs-input class="w-full text-sm" placeholder="https://example.link.com" v-model="tempCustomLink" />
                         <vs-button size="small" @click="saveCustomLink">Save Link</vs-button>
                      </div>

                      <!-- Input Form for Attachment -->
                      <div v-if="customLinkSubCategory === 'attachment'" class="flex flex-col gap-2 mt-1">
                         <!-- FIX: Added @click.stop to prevent VueDraggable from swallowing the click -->
                         <input type="file" @click.stop @change="handleFileUpload" class="text-sm border p-1 rounded w-full text-gray-700" accept=".pdf,.doc,.docx,.txt" />
                         <vs-button size="small" @click.stop="saveAttachment" :disabled="!tempAttachment">Save File</vs-button>
                      </div>
                    </div>

                    <!-- Social Flow -->
                    <div v-if="selectedTriggerCategory === 'socialConnections'">
                      <div class="flex items-center gap-2 p-1.5 mb-1 border-b cursor-pointer text-primary text-sm font-semibold hover:bg-gray-50 rounded" @click="selectedTriggerCategory = ''">
                        <feather-icon icon="ArrowLeftIcon" class="w-4 h-4" /> Back
                      </div>
                      <!-- REMOVED SCROLL CLASSES -->
                      <div class="flex flex-col gap-1">
                        <div v-for="social in connectSocialSteps" :key="social.id" 
                             class="flex items-center gap-3 p-1.5 hover:bg-gray-100 cursor-pointer rounded" @click="selectSocialStep(social.id)">
                             <feather-icon :icon="social.feather" class="w-5 h-5 text-gray-700" />
                             <span class="text-sm font-medium text-gray-700">{{ social.title }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Apps Flow (Preserving existing logic inside the new menu wrapper) -->
                    <div v-if="selectedTriggerCategory === 'apps'">
                      <!-- REMOVED SCROLL CLASSES -->
                      <div v-if="!tempAppSelected" class="flex flex-col gap-1">
                        <div class="flex items-center gap-2 p-1.5 mb-1 border-b cursor-pointer text-primary text-sm font-semibold hover:bg-gray-50 rounded" @click="selectedTriggerCategory = ''">
                          <feather-icon icon="ArrowLeftIcon" class="w-4 h-4" /> Back
                        </div>
                        <p class="text-xs font-semibold text-gray-500 mb-1 ml-1">Select App</p>
                        <template v-for="app in apps">
                          <div v-if="app.id === 'coach' || app.id === 'mentor' || app.id === 'resume-editor'" 
                               :key="app.id" 
                               class="flex items-center gap-2 p-1.5 hover:bg-gray-100 cursor-pointer rounded" 
                               @click="selectApp(app.id)">
                            <img v-if="app.id === 'coach' || app.id === 'mentor'" src="@/assets/images/coach.svg" class="w-6 h-6" />
                            <img v-else :src="app.icon" class="w-6 h-6" />
                            <span class="text-sm">{{ app.id === 'coach' || app.id === 'mentor' ? 'Coach' : app.title }}</span>
                          </div>
                        </template>
                      </div>

                      <!-- REMOVED SCROLL CLASSES -->
                      <div v-if="tempAppSelected" class="flex flex-col gap-1">
                        <div class="flex items-center gap-2 p-1.5 mb-1 border-b cursor-pointer text-primary text-sm font-semibold hover:bg-gray-50 rounded" @click="tempAppSelected = ''">
                          <feather-icon icon="ArrowLeftIcon" class="w-4 h-4" /> Back to Apps
                        </div>
                        <template v-if="tempAppSelected === 'coach'">
                          <div v-for="step in coachSteps" :key="step.id" class="text-sm p-1.5 hover:bg-gray-100 cursor-pointer rounded text-gray-700" @click="selectAppStep(step.id)">
                            {{ step.title }}
                          </div>
                        </template>
                        <template v-else>
                          <div v-for="step in (appFlow.flow[tempAppSelected] ? appFlow.flow[tempAppSelected].steps : [])" :key="step.id" v-show="step.id !== 'appInstalled'">
                            <div class="text-sm p-1.5 hover:bg-gray-100 cursor-pointer rounded text-gray-700" @click="selectAppStep(step.id)">
                              {{ step.title }}
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>

                  </div>
                </div>

                <quill-editor
                  v-model="tempColDesc"
                  :options="editorOptions"
                  class="bg-white border border-gray-300 rounded p-1 mb-2 quill-bubble-editor"
                />

                <div class="flex justify-end items-center gap-2 mt-3">
                    <small class="text-xs text-gray-500 mr-auto italic">Select text to style/link</small>
                    <vs-button type="flat" size="small" @click="editingIndex = -1">Cancel</vs-button>
                    <vs-button size="small" @click="saveColEdits(editingIndex)">Save</vs-button>
                  </div>
                </div>
              </div>
            </div> <!-- 🔴 NEW: CLOSE THE .column-drag-handle WRAPPER HERE -->


            <!-- USER CARDS CONTAINER (Now outside the drag handle) -->
            <div class="org-card kb-col bg-col scrollbarWidth mt-6 flex-1 mb-2" style="overflow-x: hidden; overflow-y: auto; min-height: 0;">
              <draggable :list="col.colUsersData" :data-colid="col._id"
                :data-col="col.title.split(' ').join('___________')" group="kb-col-items" style="min-height: 150px; height: 100%;"
                @end="userCardMoved">
                
                <template v-for="(i2, ix2) in col.colUsersData">
                  <div 
                    v-if="i2.boardUserId && getBoardUserData(i2.boardUserId)"
                    :key="i2.boardUserId || 'card-' + ix2"
                    :data-boarduserid="i2.boardUserId"
                    class="kanban-user-card cursor-pointer kb-col-item p-2 mb-3" :style="[
                      profileCompletionMap[i2.boardUserId]
                        ? {
                          borderBottom: '2px solid transparent',
                          borderImage: `linear-gradient(to right, rgba(var(--vs-primary)) ${profileCompletionMap[i2.boardUserId].profilePercent
                            }%, transparent ${profileCompletionMap[i2.boardUserId].profilePercent
                            }%) 100% 1`,
                        }
                        : {},
                    ]">
                      <user-card :boardUserData="getBoardUserData(i2.boardUserId)" :userCardView="userCardViewMaxColIndex.includes(index)
                        ? 'maximized'
                        : 'minimized'
                        " :kanbanId="kanbanData._id" :kanbanColId="col._id" @assignCoach="assignCoach" />
                  </div>
                </template>

                <div slot="footer" style="height: 100px;"></div>
                
              </draggable>
            </div>
          </div>
          
          <!-- FIX 3: Added slot="footer" so VueDraggable knows this is an appended un-draggable element -->
          <div class="vx-col" draggable="false" slot="footer">
            <div
              class="px-8 py-32 border-dashed border-2 border-slate-300 h-fit bg-slate-200 relative flex justify-center items-center rounded-lg cursor-pointer"
              v-if="!showInputforAddColumn" @click.stop="showInputforAddColumn = true">
              <p
                class="absolute flex flex-row items-center justify-center gap-4 rotate-90 transform text-2xl whitespace-no-wrap text-slate-800">
                <span class="w-6 h-6 flex justify-start items-start">
                  <svg class="w-full h-full object-cover" width="21" height="21" viewBox="0 0 21 21" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.5 10.5L10.5 10.5L7.5 10.5ZM10.5 10.5L13.5 10.5L10.5 10.5ZM10.5 10.5L10.5 7.5L10.5 10.5ZM10.5 10.5L10.5 13.5L10.5 10.5ZM10.5 1.5C11.6819 1.5 12.8522 1.73279 13.9442 2.18508C15.0361 2.63738 16.0282 3.30031 16.864 4.13604C17.6997 4.97177 18.3626 5.96392 18.8149 7.05585C19.2672 8.14778 19.5 9.3181 19.5 10.5C19.5 11.6819 19.2672 12.8522 18.8149 13.9442C18.3626 15.0361 17.6997 16.0282 16.864 16.864C16.0282 17.6997 15.0361 18.3626 13.9442 18.8149C12.8522 19.2672 11.6819 19.5 10.5 19.5C8.11305 19.5 5.82387 18.5518 4.13604 16.864C2.44821 15.1761 1.5 12.8869 1.5 10.5C1.5 8.11305 2.44821 5.82387 4.13604 4.13604C5.82387 2.44821 8.11305 1.5 10.5 1.5Z"
                      stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </span>
                Add Column
              </p>
            </div>
            <div class="cursor-pointer p-3">
              <h3 v-if="!showInputforAddColumn" @click.stop="showInputforAddColumn = true" class="items-center hidden">
                + Add a Column
              </h3>
              <div v-else>
                <vs-input class="add-col w-full mb-3" v-model="newColumnTitle" placeholder="Column Title" size="large" />
                <div class="add-col w-full mb-3">
                  <quill-editor
                    v-model="newColumnDescription"
                    :options="editorOptions"
                    class="bg-white border border-gray-300 rounded p-1 quill-bubble-editor"
                    placeholder="Add description (Highlight text to style/link)"
                  />
                </div>
                <vs-button @click.stop="addColumn(); updateKanban();" style="border-radius: 6px">Add</vs-button>
                <vs-button @click.stop="showInputforAddColumn = false" style="border-radius: 6px; background-color: white !important; color: black; box-shadow: none;" class="ml-4">Cancel</vs-button>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.bubble.css'
import { quillEditor } from 'vue-quill-editor'

import draggable from "vuedraggable";
import _ from "lodash";
import constants from "@/constants.js";
import UserCard from "./UserCard.vue";
import KanbanSkeleton from "./components/Kanban.skeleton.vue";

import appFlow from "@/assets/data/app-flow.js";
import apps from "@/assets/data/marketplace-list.js";

export default {
  props: { isAlexInstalled: { type: Boolean } },
  components: { draggable, UserCard, KanbanSkeleton, quillEditor },
  data() {
    
    // NEW FIX: Deep clone appFlow to safely inject the "Offers" step into Resume Editor
    const localAppFlow = _.cloneDeep(appFlow);
    if (localAppFlow && localAppFlow.flow && localAppFlow.flow['resume-editor']) {
      const steps = localAppFlow.flow['resume-editor'].steps;
      // Inject Offers trigger if it doesn't already exist from the main file
      if (!steps.find(step => step.id === 'offers')) {
        steps.push({
          id: 'offers',
          title: 'Offers',
          url: '/offers' // Update if your system uses a specific route structure like `/user/offers`
        });
      }
    }

    return {
      editingIndex: -1,
      userCardViewMaxColIndex: [],
      localKanbanCols: [],
      newColumnTitle: "",
      newColumnDescription: "",
      isAddColumn: false,
      showInputforAddColumn: false,
      constants,
      userCardView: "minimized",
      
      tempColTitle: "",
      tempColDesc: "",
      tempColData: {}, 

      autoSaveTimer: null,
      editorOptions: {
        theme: 'bubble',
        placeholder: 'Enter description here...',
        modules: { toolbar: [['bold', 'italic', 'underline'], ['link']] }
      },

      appFlow: localAppFlow, // Pass the patched localAppFlow instead of raw imported module
      apps,
      showAppMenu: false,
      tempAppSelected: "", 
      showTriggerMenu: false,
      selectedTriggerCategory: "",
      customLinkSubCategory: "",
      tempCustomLink: "",
      tempAttachment: null,
      tempAttachmentName: "",

      triggerOptions: [
        { id: "customLink", name: "Custom Link" },
        { id: "socialConnections", name: "Social" },
        { id: "apps", name: "Apps" }
      ],

      connectSocialSteps: [
        { id: 'twitter', title: 'Add Twitter', feather: 'TwitterIcon' },
        { id: 'discord', title: 'Add Discord', feather: 'MessageSquareIcon' },
        { id: 'linkedin', title: 'Add LinkedIn', feather: 'LinkedinIcon' },
        { id: 'facebook', title: 'Add Facebook', feather: 'FacebookIcon' },
      ],

      coachSteps: [
        { id: 'set-a-goal', title: 'Set a goal', url: 'https://coach.getboarded.com/user/add-skill' },
        { id: 'meet-your-mentor', title: 'Meet your coach', url: 'https://coach.getboarded.com/user/my-coach/profile/view' },
        { id: 'invite-advisors', title: 'Invite Advisors', url: 'https://coach.getboarded.com/user/my-rater/rater-list' },
        { id: 'motivation-surveys', title: 'Motivation Survey', url: 'https://coach.getboarded.com/user/coaching-experience' }
      ]
    };
  },
  computed: {
    // FIX: Safely construct the app list without touching broken Object getters that trigger Webpack errors
    safeFilteredApps() {
      const list = [];
      this.apps.forEach(app => {
        // Completely bypass the original object for coach/mentor to prevent triggering broken require() functions
        if (app.id === 'mentor' || app.id === 'coach') {
          if (!list.find(a => a.id === 'coach')) {
            list.push({
              id: 'coach',
              title: 'Coach',
              icon: 'https://img.icons8.com/color/48/000000/training.png'
            });
          }
        } else if (['resume-editor'].includes(app.id)) {
          list.push(app);
        }
      });
      return list;
    },
    currentBoard() { return this.$store.state.boards.currentBoard; },
    kanbanData() { return this.$store.state.kanban.kanbanData; },
    currentBoardUsers() { return this.$store.state.boards.currentBoardUsers; },
    userData() { return this.$store.state.users.data; },
    profileCompletionMap() { return this.$store.state.boards.profileCompletionMap || {}; },
    isPaidCoachBoard() {
      if (!this.currentBoard) return false;
      return this.currentBoard.paymentStatus === 'paid' || 
             this.currentBoard.isCoach === true ||
             (this.currentBoard.slug && this.currentBoard.slug.includes('coach'));
    }
  },
  watch: {
    currentBoard(newVal) {
      if (newVal) {
        this.fetchKanbanData();
        this.$store.dispatch("boards/fetchBoardUserProfileAnalytics", newVal._id);
      }
    },
    currentBoardUsers(newVal) {
      if (newVal) this.fetchKanbanData();
    },
    kanbanData(newVal) {
      if (newVal) {
        this.localKanbanCols = JSON.parse(JSON.stringify(newVal.cols));
        this.$forceUpdate();
      }
    },
    localKanbanCols: {
      deep: true,
      handler(newVal) {
        if (newVal && this.kanbanData && this.kanbanData._id) {
          if (!_.isEqual(newVal, this.kanbanData.cols)) {
            if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
            this.autoSaveTimer = setTimeout(() => { this.updateKanban(); }, 800);
          }
        }
      },
    },
    profileCompletionMap: { deep: true, handler() { this.$forceUpdate(); } },
  },
  methods: {
    // FIX: Safe getter for App Icon logic to prevent webpack crash
    getSafeApp(id) {
      if (!id) return null;
      // Immediately return fixed format for mentor or coach, avoiding Webpack triggers completely
      if (id === 'mentor' || id === 'coach') {
        return {
          id: 'coach',
          title: 'Coach',
          icon: 'https://img.icons8.com/color/48/000000/training.png'
        };
      }
      return this.apps.find(a => a.id === id) || null;
    },
    
    getAppFlow(appId, appStepId) {
      // Handles both mentor & coach checks for backwards compatibility with database entries
      if (appId === 'mentor' || appId === 'coach') {
        return this.coachSteps.find(step => step.id === appStepId);
      }
      if(!appId || !this.appFlow.flow[appId]) return null;
      return this.appFlow.flow[appId].steps.find((step) => step.id === appStepId);
    },

    resetTriggerState() {
      this.selectedTriggerCategory = "";
      this.customLinkSubCategory = "";
      this.tempCustomLink = "";
      this.tempAttachment = null;
      this.tempAttachmentName = "";
      this.tempAppSelected = "";
    },
    
    saveCustomLink() {
      if (!this.tempCustomLink) return;
      this.$set(this.tempColData, 'type', 'customLink');
      this.$set(this.tempColData, 'customLink', this.tempCustomLink);
      this.showTriggerMenu = false;
    },
    
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (file) {
        this.tempAttachment = file;
        this.tempAttachmentName = file.name;
      }
    },
    
    async saveAttachment() {
      if (!this.tempAttachment) return;
      
      this.$vs.loading(); 
      
      try {
        let formData = new FormData();
        formData.append("file", this.tempAttachment);

        // Call the new Python API endpoint
        const uploadRes = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/v2/kanban-attachments/upload`, 
          formData, 
          {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            }
          }
        );
        
        // Extract the generated URL from Python
        const serverFileUrl = uploadRes.data.fileUrl; 

        // Save the URL to the customLink property
        this.$set(this.tempColData, 'type', 'customLink');
        this.$set(this.tempColData, 'customLink', serverFileUrl); 
        this.$set(this.tempColData, 'attachmentName', this.tempAttachmentName);
        
        this.showTriggerMenu = false;
        
      } catch (err) {
        console.error("File upload failed:", err);
        this.$vs.notify({
          title: 'Upload Error',
          text: 'Failed to upload attachment to server.',
          color: 'danger'
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    selectSocialStep(stepId) {
      this.$set(this.tempColData, 'type', 'socialConnections');
      this.$set(this.tempColData, 'socialStepData', stepId);
      this.showTriggerMenu = false;
    },

    getSocialStepById(id) {
      return this.connectSocialSteps.find((step) => step.id === id) || null;
    },

    selectApp(appId) { this.tempAppSelected = appId; },

    selectAppStep(stepId) {
      this.$set(this.tempColData, 'type', 'apps');
      this.$set(this.tempColData, 'appSelected', this.tempAppSelected);
      this.$set(this.tempColData, 'appStepData', stepId);
      this.showTriggerMenu = false;
      this.tempAppSelected = "";
    },

    removeAppTrigger() { this.tempColData = {}; },

    handleBoardAppInstallation() {
      let appsToInstall = [...(this.currentBoard.installedApps || [])];
      this.localKanbanCols.forEach(col => {
        if (col.colData && col.colData.appSelected && !appsToInstall.includes(col.colData.appSelected)) {
          appsToInstall.push(col.colData.appSelected);
        }
      });

      const isCoachBoard = this.currentBoard.slug && this.currentBoard.slug.includes('coach');
      if (isCoachBoard && !appsToInstall.includes('coach')) {
        appsToInstall.push('coach');
      }

      if (appsToInstall.length > 0) {
        this.$store.dispatch("boards/updateBoard", {
          boardObj: { installedApps: appsToInstall, _boardId: this.currentBoard._id },
          organizationId: this.currentBoard.organizationId,
        });
      }
    },

    editCol(col, index) {
      if (this.editingIndex !== index) {
        this.editingIndex = index;
        this.tempColTitle = col.title;
        this.tempColDesc = col.description;
        this.tempColData = col.colData ? JSON.parse(JSON.stringify(col.colData)) : {}; 
        this.showTriggerMenu = false;
        this.resetTriggerState();
      } else {
        this.editingIndex = -1;
      }
    },

    saveColEdits(index) {
      if (index !== -1) {
        this.localKanbanCols[index].title = this.tempColTitle;
        this.localKanbanCols[index].description = this.tempColDesc;
        this.localKanbanCols[index].colData = this.tempColData;

        this.tempColTitle = "";
        this.tempColDesc = "";
        this.tempColData = {};
        this.editingIndex = -1;
        
        this.handleBoardAppInstallation();
        this.updateKanban();
      }
    },

    checkColMove(evt) {
      this.editingIndex = -1;
      
      // 1. SAFELY check if the contexts exist before trying to read .element
      if (!evt || !evt.draggedContext || !evt.relatedContext) {
        return false;
      }

      const draggedEl = evt.draggedContext.element;
      const relatedEl = evt.relatedContext.element;

      // 2. SAFELY check if the elements inside the contexts exist
      if (!draggedEl || !relatedEl) {
        return false;
      }
      
      // 3. Now it is 100% safe to check your specific logic
      if (!draggedEl._id) return false;
      if (draggedEl.title === "Initial Phase") return false;
      if (evt.draggedContext.index === 0 || evt.relatedContext.index === 0) return false;
      
      return true;
    },
    getBoardUserData(boardUserId) { return _.find(this.currentBoardUsers, (cbu) => cbu._id === boardUserId); },
    addColumn() {
      this.localKanbanCols.push({ title: this.newColumnTitle || "Column Name", description: this.newColumnDescription || "", colUsersData: [] });
      this.isAddColumn = true;
      this.showInputforAddColumn = false;
      this.newColumnTitle = "";
      this.newColumnDescription = "";
    },
    fetchKanbanData() { this.$store.dispatch("kanban/getKanbanDataV2", this.currentBoard._id); },
    updateKanban() {
      if (!this.kanbanData || !this.kanbanData._id) {
        this.$store.dispatch("boards/createKanban", { boardId: this.currentBoard._id, cols: this.localKanbanCols }).then(() => this.fetchKanbanData());
        return;
      }
      this.$store.dispatch("kanban/updateKanbanV2", { boardId: this.currentBoard._id, cols: this.localKanbanCols }).then(() => this.fetchKanbanData());
    },
    deleteColumn(index) {
      this.localKanbanCols.splice(index, 1);
      this.updateKanban();
    },
    removeUserCardViewMaxColIndex(index) {
      const i = this.userCardViewMaxColIndex.findIndex(indx => indx === index);
      this.userCardViewMaxColIndex.splice(i, 1);
    },
    userCardMoved(uc) {
      const fromColId = uc.from.getAttribute("data-colid");
      const toColId = uc.to.getAttribute("data-colid");
      if (fromColId === toColId) return;

      const fromCol = uc.from.getAttribute("data-col").split("___________").join(" ");
      const toCol = uc.to.getAttribute("data-col").split("___________").join(" ");
      const draggedBoardUserId = uc.item.getAttribute("data-boarduserid");
      const boardUserData = this.getBoardUserData(draggedBoardUserId);
      
      if (boardUserData && boardUserData.userData) {
        const boardUserName = `${boardUserData.userData.firstName} ${boardUserData.userData.lastName}`;
        this.$store.dispatch("organization/logOrganizationActivity", {
          organizationId: this.currentBoard.organizationId,
          logObj: { logText: `Moved ${boardUserName} from ${fromCol} to ${toCol} on ${this.currentBoard.title} Board`, status: "active", boardId: this.currentBoard._id, boardUserId: boardUserData._id, userId: this.userData._id },
        });
        
        const userStepPayload = { boardId: this.currentBoard._id, kanbanColId: this.localKanbanCols.filter(c => c.title == toCol).map((c) => c._id)[0], userId: boardUserData.userId, reviewStatus: 'PENDING' }
        const fromColIndex = this.localKanbanCols.map(c => c._id).indexOf(fromColId);
        const toColIndex = this.localKanbanCols.map(c => c._id).indexOf(toColId);
        
        if (fromColIndex > toColIndex) {
          userStepPayload.kanbanColId = fromColId;
          userStepPayload.reviewStatus = 'PENDING';
          this.$store.dispatch("boards/updateBoardStep", userStepPayload).then(res => {
              if (res.status == 'SUCCESS') {
                userStepPayload.kanbanColId = toColId;
                userStepPayload.reviewStatus = "USER_ACCEPTED";
                this.$store.dispatch("boards/updateBoardStep", userStepPayload);
              }
            })
        }
        else {
          userStepPayload.kanbanColId = fromColId;
          userStepPayload.reviewStatus = "USER_ACCEPTED";
          this.$store.dispatch("boards/updateBoardStep", userStepPayload).then(res => {
              if (res.status == 'SUCCESS') {
                userStepPayload.kanbanColId = toColId;
                userStepPayload.reviewStatus = "USER_ACCEPTED";
                this.$store.dispatch("boards/updateBoardStep", userStepPayload);
              }
            })
        }
      }
    },
    assignCoach(traineeId) { this.$emit("assignCoach", traineeId); },
  },
  created() {
    if (this.currentBoard && this.currentBoard._id) {
      this.fetchKanbanData();
      this.$store.dispatch("boards/fetchBoardUserProfileAnalytics", this.currentBoard._id);
    }
  },
};
</script>

<style scoped lang="scss">
.kb-col-head { border-bottom: 3px solid rgba(var(--vs-primary), 1); }
::v-deep .html-content a { color: #1976d2 !important; text-decoration: underline !important; cursor: pointer; }
::v-deep .html-content strong { font-weight: bold !important; }
::v-deep .html-content em { font-style: italic !important; }
::v-deep .html-content u { text-decoration: underline !important; }
::v-deep .html-content p { display: inline !important; margin: 0 !important; }
::v-deep .quill-bubble-editor .ql-editor { min-height: 80px; font-size: 14px; }
</style>

<style scoped>
.kb-main { height: calc(100vh - 13.6rem); border-radius: 8px; }
.bg-main { background-color: transparent !important; }
.kanban-user-card { background: #ffffff; border-radius: 8px; border-bottom-left-radius: 0; border-bottom-right-radius: 0; box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.05); }
.vx-col:nth-child(1) .bg-col .kanban-user-card { background: #ffffff; box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.05); }
.vx-col:nth-child(1) .bg-col { background-color: transparent; }
.bg-col { background: transparent; border-radius: 4px; }
.org-card.kb-col { width: 320px !important; box-shadow: none; }
.add-col { width: 320px !important; height: fit-content !important; border: 1px dashed #b2bac8; border-radius: 8px; }
.org-card.kb-col.vx-card>.vx-card__collapsible-content .vx-card__body { padding: 0 0 1.5rem 0 !important; }
.kb-col-head { padding: 1.5rem; background: #dfe3eb; border-radius: 4px; }
.scrollbarWidth::-webkit-scrollbar { width: 10px; }
.scrollbarWidth::-webkit-scrollbar-track { -webkit-box-shadow: inset 0 0 6px rgba(41, 39, 39, 0.3); border-radius: 10px; }
.scrollbarWidth::-webkit-scrollbar-thumb { border-radius: 10px; -webkit-box-shadow: inset 0 0 20px rgb(193 193 193); }
</style>

<style>
/* 1. Pushes the horizontal scrollbar down below the columns */
.vx-row.bg-main {
  padding-bottom: 20px !important;
}

/* 2. Adds padding inside the columns so the last card clears the bottom */
.bg-col > div {
  padding-bottom: 35px !important;
}
</style>